import React, { Component } from 'react';
import PropTypes from 'prop-types';

import SidebarListItem from './SidebarListItem';

class Sidebar extends Component {
	constructor() {
		super();
		this.trailheadList = null;
		this.sidebarEl = null;
		this.renderListItems = this.renderListItems.bind(this);
		this.handleListItemClick = this.handleListItemClick.bind(this);
		this.handleToggleBtnClick = this.handleToggleBtnClick.bind(this);
		this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
	}

	componentDidMount() {
		if (this.sidebarEl) {
			this.sidebarEl.addEventListener(
				'transitionend',
				this.handleTransitionEnd
			);
		}
	}

	componentWillUnmount() {
		if (this.sidebarEl) {
			this.sidebarEl.removeEventListener(
				'transitionend',
				this.handleTransitionEnd
			);
		}
	}

	componentDidUpdate(prevProps) {
		const { selected, selector, sidebar } = this.props;

		if (prevProps.selected !== selected && selector === 'map') {
			const targetLi = document.getElementById(`list-item-${selected}`);
			if (targetLi) {
				const offset = targetLi.offsetTop;
				this.trailheadList.scrollTop = offset;
				// Focus the selected item's button
				const btn = targetLi.querySelector('.more-info');
				if (btn) {
					setTimeout(() => btn.focus(), 350);
				}
			}
		}

		// Closing: focus the burger button after close animation
		if (!sidebar && prevProps.sidebar) {
			setTimeout(() => {
				const burger = document.querySelector('.sidebar-menu-burger');
				if (burger) {
					burger.focus();
				}
			}, 350);
		}

		// Opening: show element first, then let the class change animate
		if (sidebar && !prevProps.sidebar && this.sidebarEl) {
			// Remove display:none while still in 'closed' position
			this.sidebarEl.style.display = '';
			// Force reflow so browser registers the closed position
			void this.sidebarEl.offsetHeight;
			// Now React has already set the 'opened' class, but the
			// browser hasn't painted yet. Force it to recalculate.
			this.sidebarEl.classList.remove('opened');
			void this.sidebarEl.offsetHeight;
			this.sidebarEl.classList.add('opened');

			// Focus the first interactive element after the open animation
			setTimeout(() => {
				if (selected) {
					const targetLi = document.getElementById(`list-item-${selected}`);
					const btn = targetLi && targetLi.querySelector('.more-info');
					if (btn) {
						btn.focus();
						return;
					}
				}
				// No selection — focus the first list item
				const firstItem = this.sidebarEl.querySelector('.SidebarListItem .more-info');
				if (firstItem) {
					firstItem.focus();
				}
			}, 350);
		}
	}

	handleTransitionEnd(e) {
		if (e.propertyName !== 'transform') {
			return;
		}
		if (!this.props.sidebar && this.sidebarEl) {
			this.sidebarEl.style.display = 'none';
		}
	}

	handleCloseClick() {
		this.props.sidebarClose();
	}

	handleListItemClick(id) {
		this.props.selectTripTrailhead(id, 'list');
	}

	renderListFromToggled(toggled) {
		const { selected } = this.props;
		const type = this.props[toggled]; // either "trips" or "trailheads"

		if (!type.length) {
			return;
		}

		return type
			.sort((a, b) => {
				// sort the trailheads alphabetically
				const titleA = a.title.rendered.toUpperCase();
				const titleB = b.title.rendered.toUpperCase();

				if (titleA < titleB) {
					return -1;
				}
				if (titleA > titleB) {
					return 1;
				}
				return 0;
			})
			.map((item) => {
				const { acf, id, slug, title } = item;

				return (
					<SidebarListItem
						key={id}
						handleListItemClick={this.handleListItemClick}
						sidebarOpen={this.props.sidebar}
						{...{ acf, id, selected, slug, title, toggled }}
					/>
				);
			});
	}

	renderListItems() {
		const { fetchingData, trailheadsToggled } = this.props;

		if (fetchingData) {
			const type = trailheadsToggled ? 'trailheads' : 'trips';

			return (
				<li className="SidebarListItem">
					<p className="title">{`Loading ${type}...`}</p>
				</li>
			);
		}

		if (trailheadsToggled) {
			return this.renderListFromToggled('trailheads');
		}
		return this.renderListFromToggled('trips');
	}

	handleToggleBtnClick() {
		const { fetchingData, deselectTripTrailhead, toggleTraiheadsTrips } =
			this.props;
		// prevent user from toggling when fetching data
		if (fetchingData) {
			return;
		}
		// deselect any currently selected trip / trailhead
		deselectTripTrailhead();
		toggleTraiheadsTrips();
	}

	render() {
		const { sidebar, trailheadsToggled } = this.props;
		const sidebarClassName = sidebar ? 'Sidebar opened' : 'Sidebar closed';

		return (
			<div
				key="sidebar-main"
				className={sidebarClassName}
				aria-hidden={!sidebar}
				ref={(_) => {
					this.sidebarEl = _;
				}}
			>
				<div className="sidebar-header">
					<div className="sidebar-header-inner">
						<button
							className="sidebar-title"
							tabIndex={sidebar ? 0 : -1}
							aria-pressed={trailheadsToggled}
							onClick={this.handleToggleBtnClick}
						>
							<h4 className={trailheadsToggled ? 'active' : ''}>
								Trailheads
							</h4>
						</button>
						<button
							className="sidebar-title"
							tabIndex={sidebar ? 0 : -1}
							aria-pressed={!trailheadsToggled}
							onClick={this.handleToggleBtnClick}
						>
							<h4 className={!trailheadsToggled ? 'active' : ''}>
								Trips
							</h4>
						</button>
						<span className="sidebar-close">
							<button
								aria-label="Close Trailhead and Trips Side Menu"
								tabIndex={sidebar ? 0 : -1}
								onClick={() => this.handleCloseClick()}
							>
								×
							</button>
						</span>
					</div>
				</div>
				<ul
					className="trailheads-list"
					ref={(_) => {
						this.trailheadList = _;
					}}
				>
					{this.renderListItems()}
				</ul>
			</div>
		);
	}
}

Sidebar.propTypes = {
	fetchingData: PropTypes.bool.isRequired,
	toggleTraiheadsTrips: PropTypes.func.isRequired,
	trailheads: PropTypes.array,
	trips: PropTypes.array,
	trailheadsToggled: PropTypes.bool.isRequired,
	selected: PropTypes.number,
	selector: PropTypes.string,
	sidebar: PropTypes.bool.isRequired,
	sidebarClose: PropTypes.func.isRequired,
	selectTripTrailhead: PropTypes.func.isRequired,
	deselectTripTrailhead: PropTypes.func.isRequired,
};

export default Sidebar;



// WEBPACK FOOTER //
// ./map/components/Sidebar.js